/**
 * AssignedTaskCreatePage — form to create a task manually assigned to either
 * a customer or an agent.
 *
 * Route: /dashboard/cloudgm/assigned-tasks/new?target=customer|agent
 *
 * Layout (matches the user's brief):
 *   1) الهدف   — Customer: MUST be registered (search DB). Agent: registered OR new (email + phone kept for future linking).
 *   2) تفاصيل الخدمة — service dropdown (from services-catalog endpoint) + notes textarea.
 *   3) خصائص الطلب — priority (4 levels: low/medium/high/urgent) + request source + due date.
 *   4) منفذ المهمة — dropdown of admin/staff (mandatory).
 *   5) Submit → creates the assigned_task + mirrored service_request (auto-approved).
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Loader2, ArrowLeft, User, Store, Search, MessageCircle, UserPlus, UserCheck,
  Paperclip, FileText, Image as ImageIcon, X,
} from 'lucide-react';

import DashboardHeader from '../../components/DashboardHeader';
import BreadcrumbNav from '../../components/BreadcrumbNav';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useLanguage } from '../../context/LanguageContext';
import { getAuthHeaders } from '../../utils/authHeaders';
import { isValidEmail, isValidPhone } from '../../utils/validators';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_LEVELS = [
  { key: 'low',    ar: 'منخفضة', en: 'Low',    color: '#71717a' },
  { key: 'medium', ar: 'متوسطة', en: 'Medium', color: '#f59e0b' },
  { key: 'high',   ar: 'عاجلة',  en: 'Urgent', color: '#ef4444' },
];

const REQUEST_SOURCES = [
  { key: 'whatsapp',  ar: 'واتساب',        en: 'WhatsApp' },
  { key: 'phone',     ar: 'اتصال هاتفي',  en: 'Phone call' },
  { key: 'email',     ar: 'بريد إلكتروني',en: 'Email' },
  { key: 'in_person', ar: 'حضور شخصي',    en: 'In person' },
  { key: 'referral',  ar: 'إحالة',         en: 'Referral' },
  { key: 'other',     ar: 'أخرى',          en: 'Other' },
];

// ── Customer-platform MAIN service categories (Feb 2026, iter-173) ───
// LEGACY fallback used only if the /api/admin/service-catalog/customer
// endpoint returns nothing.  The admin can now edit both lists live
// from /control/cloudgm/customer-dropdowns.
const CUSTOMER_MAIN_SERVICES_FALLBACK = [
  {
    id: 'printing-docs',
    name_ar: 'الطباعة وتخليص المستندات',
    name_en: 'Printing & Document Clearing',
    sub_services: [
      { id: 'visa-id',           name_ar: 'طلبات التأشيرة والهوية',                  name_en: 'Visa & Emirates ID' },
      { id: 'tourist-visa',      name_ar: 'التأشيرات السياحية لجميع الدول',          name_en: 'Tourist Visas — All Countries' },
      { id: 'doc-certification', name_ar: 'تصديق المستندات',                         name_en: 'Document Certification' },
      { id: 'driving-license',   name_ar: 'خدمات رخصة القيادة',                       name_en: 'Driving License Services' },
      { id: 'printing-docs-general', name_ar: 'الطلبات العامة — طباعة وتخليص',       name_en: 'General Printing & Documents' },
    ],
  },
  { id: 'company-formation', name_ar: 'تأسيس الشركات',              name_en: 'Company Formation',          sub_services: [] },
  { id: 'tax-agent',         name_ar: 'خدمات الوكيل الضريبي',       name_en: 'Tax Agent Services',         sub_services: [] },
  { id: 'bill-payment',      name_ar: 'خدمات دفع الفواتير',         name_en: 'Bill Payment Services',      sub_services: [] },
  { id: 'legal-translation', name_ar: 'خدمات الترجمة القانونية',    name_en: 'Legal Translation Services', sub_services: [] },
];

// Special ID for the always-present "Other" option, both at main + sub level.
const OTHER_ID = '__other__';

export default function AssignedTaskCreatePage() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const targetType = params.get('target') === 'agent' ? 'agent' : 'customer';
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const isCustomer = targetType === 'customer';
  const accentColor = isCustomer ? '#3b82f6' : '#8b5cf6';
  const TargetIcon = isCustomer ? User : Store;

  const [form, setForm] = useState({
    target_id: null,
    target_name: '',
    target_phone: '',
    target_email: '',
    // Service picked from the catalog
    service_id: '',
    service_name_ar: '',
    service_name_en: '',
    // Feb 2026 — customer target only: main category + optional sub-service
    sub_service_id: '',
    sub_service_name_ar: '',
    sub_service_name_en: '',
    // Feb 2026 (iter-183) — Fees moved entirely OUT of the creation form to the
    // detail page. Keep these keys in form state (empty defaults) so the submit
    // body remains schema-compatible with the backend model.
    gov_procedures: [],
    service_fee: '',
    fee_model: 'service_only',
    // Free-text override used when the admin picks "أخرى" (Other) in the sub
    // (or main when the main has no subs).
    custom_service_name: '',
    // Additional description of the task (free text)
    description_ar: '',
    description_en: '',
    priority: 'medium',
    request_date: new Date().toISOString().slice(0, 10),   // default = today; admin overrides for retroactive
    request_source: 'whatsapp',
    request_note: '',
    // iter-188 — Requestor identity (AGENT tasks only). Person submitting on
    // behalf of the target agent — all optional but validated when supplied.
    requestor_name: '',
    requestor_email: '',
    requestor_phone: '',
    assigned_to_id: '',
    assigned_to_name: '',
    assigned_to_email: '',
    attachments: [],   // { name, mime_type, size, data_url }
  });

  // Registration-mode toggle (agent only)
  const [agentMode, setAgentMode] = useState('registered');  // 'registered' | 'new'

  // Target search
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const [servicesCatalog, setServicesCatalog] = useState([]);
  // Feb 2026 — dynamic dropdown catalog fed by /api/admin/service-catalog/{scope}.
  // Falls back to the hard-coded list for customer if the API returns nothing.
  const [catalog, setCatalog] = useState([]);
  const [staff, setStaff] = useState([]);
  // iter-193 (Feb 2026) — separate loading + error signals for the staff
  // dropdown so a partial catalog failure never leaves the executor list empty.
  const [staffLoading, setStaffLoading] = useState(true);
  const [staffLoadError, setStaffLoadError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState(null);   // {request_no_display, service_request_id} after create

  // iter-193 — isolated staff loader with a single retry, so the executor
  // dropdown never depends on unrelated catalog endpoints.
  const loadStaff = useCallback(async () => {
    setStaffLoading(true);
    setStaffLoadError(false);
    const fetchOnce = () => axios.get(
      `${API_URL}/api/admin/assigned-tasks/staff`,
      { headers: getAuthHeaders() },
    );
    try {
      let res;
      try {
        res = await fetchOnce();
      } catch (_e) {
        // Retry once after 700ms — recovers from transient network hiccups.
        await new Promise(r => setTimeout(r, 700));
        res = await fetchOnce();
      }
      const rows = (res.data?.data || []).filter(p => p && p.id && (p.email || p.name));
      setStaff(rows);
      if (rows.length === 0) setStaffLoadError(true);
    } catch (e) {
      setStaffLoadError(true);
      setStaff([]);
    } finally {
      setStaffLoading(false);
    }
  }, []);

  // Load catalogs — staff is loaded INDEPENDENTLY of the other two so a
  // catalog failure never blocks executor selection.
  useEffect(() => {
    loadStaff();
    (async () => {
      try {
        const [svc, cat] = await Promise.all([
          axios.get(`${API_URL}/api/admin/assigned-tasks/services-catalog`, {
            headers: getAuthHeaders(), params: { target_type: targetType },
          }).catch(() => ({ data: { data: [] } })),
          axios.get(`${API_URL}/api/admin/service-catalog/${targetType}`, {
            headers: getAuthHeaders(),
          }).catch(() => ({ data: { data: [] } })),
        ]);
        setServicesCatalog(svc.data.data || []);
        const dyn = cat.data?.data || [];
        // Customer target ⇒ fall back to hard-coded list when catalog is empty.
        if (isCustomer && dyn.length === 0) {
          setCatalog(CUSTOMER_MAIN_SERVICES_FALLBACK);
        } else {
          setCatalog(dyn);
        }
      } catch { toast.error(t('فشل تحميل قوائم الخدمات', 'Failed to load service catalogs')); }
    })();
  }, [targetType, isAr, isCustomer]);

  // Reset if switching modes
  useEffect(() => {
    if (agentMode === 'new') {
      setForm(f => ({ ...f, target_id: null }));
      setSearchQ(''); setSearchResults([]);
    }
  }, [agentMode]);

  // Debounced target search
  useEffect(() => {
    const canSearch = isCustomer || (targetType === 'agent' && agentMode === 'registered');
    if (!canSearch || !searchQ.trim() || searchQ.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await axios.get(`${API_URL}/api/admin/assigned-tasks/targets/search`, {
          headers: getAuthHeaders(),
          params: { type: targetType, q: searchQ.trim() },
        });
        setSearchResults(r.data.data || []);
      } catch { setSearchResults([]); }
      finally { setSearching(false); }
    }, 200);  // iter-188 — snappier debounce (was 300ms)
    return () => clearTimeout(timer);
  }, [searchQ, targetType, agentMode, isCustomer]);

  const pickTarget = useCallback((u) => {
    setForm(f => ({
      ...f,
      target_id: u.id,
      target_name: u.name || '',
      target_email: u.email || '',
      target_phone: u.phone || '',
    }));
    setSearchResults([]);
    setSearchQ(u.name || u.email || '');
  }, []);

  const pickService = (id) => {
    if (!id) {
      setForm(f => ({
        ...f,
        service_id: '', service_name_ar: '', service_name_en: '',
        sub_service_id: '', sub_service_name_ar: '', sub_service_name_en: '',
        custom_service_name: '',
      }));
      return;
    }
    // Feb 2026 — "أخرى" pseudo-option (both scopes)
    if (id === OTHER_ID) {
      setForm(f => ({
        ...f,
        service_id: OTHER_ID,
        service_name_ar: 'أخرى',
        service_name_en: 'Other',
        sub_service_id: '', sub_service_name_ar: '', sub_service_name_en: '',
        custom_service_name: '',
      }));
      return;
    }
    // Both targets now share the same dynamic `catalog` (main + sub).
    const svc = catalog.find(s => s.id === id);
    if (svc) {
      setForm(f => ({
        ...f,
        service_id: svc.id,
        service_name_ar: svc.name_ar,
        service_name_en: svc.name_en || svc.name_ar,
        // Reset sub-service every time the main category changes
        sub_service_id: '', sub_service_name_ar: '', sub_service_name_en: '',
        custom_service_name: '',
      }));
    }
  };

  const pickSubService = (id) => {
    if (!id) {
      setForm(f => ({ ...f, sub_service_id: '', sub_service_name_ar: '', sub_service_name_en: '', custom_service_name: '' }));
      return;
    }
    if (id === OTHER_ID) {
      setForm(f => ({
        ...f,
        sub_service_id: OTHER_ID,
        sub_service_name_ar: 'أخرى',
        sub_service_name_en: 'Other',
        custom_service_name: '',
      }));
      return;
    }
    const parent = catalog.find(s => s.id === form.service_id);
    const sub = parent?.sub_services?.find(x => x.id === id);
    if (sub) {
      setForm(f => ({
        ...f,
        sub_service_id: sub.id,
        sub_service_name_ar: sub.name_ar,
        sub_service_name_en: sub.name_en || sub.name_ar,
        custom_service_name: '',
      }));
    }
  };

  const pickExecutor = (id) => {
    if (!id) {
      setForm(f => ({ ...f, assigned_to_id: '', assigned_to_name: '', assigned_to_email: '' }));
      return;
    }
    const p = staff.find(x => x.id === id);
    if (p) {
      setForm(f => ({ ...f, assigned_to_id: p.id, assigned_to_name: p.name || p.email, assigned_to_email: p.email }));
    }
  };

  // ── Attachments ─────────────────────────────────────
  const MAX_ATTACHMENTS = 15;
  const MAX_ATTACHMENT_MB = 8;

  const onAttachmentPicked = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (form.attachments.length + files.length > MAX_ATTACHMENTS) {
      toast.error(t(`الحد الأقصى ${MAX_ATTACHMENTS} مرفقات`, `Max ${MAX_ATTACHMENTS} attachments`));
      return;
    }
    const acceptedTypes = /^(application\/pdf|image\/(png|jpe?g|webp|heic|heif))$/i;
    const newAtts = [];
    for (const f of files) {
      if (!acceptedTypes.test(f.type)) {
        toast.error(t(`${f.name}: نوع غير مدعوم — PDF أو صور فقط`,
                       `${f.name}: unsupported — PDF or image only`));
        continue;
      }
      if (f.size > MAX_ATTACHMENT_MB * 1024 * 1024) {
        toast.error(t(`${f.name}: أكبر من ${MAX_ATTACHMENT_MB} ميجا`,
                       `${f.name}: exceeds ${MAX_ATTACHMENT_MB} MB`));
        continue;
      }
      const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(f);
      });
      newAtts.push({
        name:      f.name,
        label:     '',   // user must supply a meaningful name — do NOT prefill from filename
        mime_type: f.type,
        size:      f.size,
        data_url:  dataUrl,
      });
    }
    if (newAtts.length) {
      setForm(f => ({ ...f, attachments: [...f.attachments, ...newAtts] }));
    }
  };

  const renameAttachment = (idx, label) => {
    setForm(f => ({
      ...f,
      attachments: f.attachments.map((a, i) => (i === idx ? { ...a, label } : a)),
    }));
  };

  const removeAttachment = (idx) => {
    setForm(f => ({
      ...f,
      attachments: f.attachments.filter((_, i) => i !== idx),
    }));
  };

  // Validation
  const targetOk = form.target_name.trim() && (
    (isCustomer && !!form.target_id)  // customer must be registered
    || (targetType === 'agent' && agentMode === 'registered' && !!form.target_id)
    || (targetType === 'agent' && agentMode === 'new' && (form.target_email.trim() || form.target_phone.trim()))
  );
  // Feb 2026 — when the admin picks "Other" in either level, the free-text
  // service-name field becomes REQUIRED (per user spec item G).
  const otherPicked = form.service_id === OTHER_ID || form.sub_service_id === OTHER_ID;
  const serviceOk   = otherPicked
    ? form.custom_service_name.trim().length >= 2
    : !!form.service_name_ar && form.service_id !== OTHER_ID;
  const executorOk = !!form.assigned_to_id;
  const attachmentsOk = form.attachments.every(a => (a.label || '').trim().length > 0);
  // Feb 2026 (iter-183) — Fees no longer collected in this form; procedures
  // + service fee are configured from the task detail page after creation.
  // Feb 2026 — iter-179: keep the button always-clickable so admins get an EXPLICIT
  // toast telling them what's missing (Production RCA showed 0 POSTs firing because
  // the button was silently disabled and users didn't notice the tiny amber hint).
  const canSubmit = !submitting;

  const submit = async () => {
    if (submitting) return;
    // Explicit validation with loud toasts — no more silent bail-out.
    if (!targetOk) {
      if (isCustomer && !form.target_id) {
        toast.error(t('اختر عميلاً مسجّلاً من قائمة البحث', 'Pick a REGISTERED customer from the search list'));
      } else if (targetType === 'agent' && agentMode === 'registered' && !form.target_id) {
        toast.error(t('اختر وكيلاً مسجّلاً من قائمة البحث', 'Pick a REGISTERED agent from the search list'));
      } else if (targetType === 'agent' && agentMode === 'new' && !form.target_email.trim() && !form.target_phone.trim()) {
        toast.error(t('أدخل البريد الإلكتروني أو رقم الهاتف للوكيل الجديد', 'Enter the new agent\'s email or phone'));
      } else if (!form.target_name.trim()) {
        toast.error(t('اسم المستهدف مطلوب', 'Target name is required'));
      } else {
        toast.error(t('حدد المستهدف المطلوب', 'Pick a valid target'));
      }
      return;
    }
    if (!serviceOk) {
      if (otherPicked && form.custom_service_name.trim().length < 2) {
        toast.error(t('اكتب اسم الخدمة (٢ أحرف على الأقل)', 'Type the custom service name (min 2 chars)'));
      } else {
        toast.error(t('اختر الخدمة الرئيسية', 'Pick the main service'));
      }
      return;
    }
    if (!executorOk) {
      toast.error(t('اختر منفذ المهمة (٤) — الطلب لن يصل لأي أدمن بدون تحديد المنفذ',
                    '(4) Pick a task executor — the task will not reach any admin without one'));
      return;
    }
    if (!attachmentsOk) {
      const missingIdx = form.attachments.findIndex(a => (a.label || '').trim().length === 0);
      toast.error(t(`المرفق رقم ${missingIdx + 1} بحاجة لاسم — أدخل اسم المرفق قبل الحفظ`,
                    `Attachment #${missingIdx + 1} needs a name — enter its name before saving`));
      return;
    }
    // iter-188 — Validate optional requestor fields when supplied (agent only).
    if (targetType === 'agent') {
      if (form.requestor_email && !isValidEmail(form.requestor_email)) {
        toast.error(t('بريد المقدم الإلكتروني غير صحيح', 'Requestor email is not valid'));
        return;
      }
      if (form.requestor_phone && !isValidPhone(form.requestor_phone)) {
        toast.error(t('رقم هاتف المقدم غير صحيح', 'Requestor phone is not valid'));
        return;
      }
    }
    setSubmitting(true);
    try {
      // Resolve the final service names — when "Other" is used, the free-text
      // becomes the service name at the correct level; the other level keeps
      // whatever the catalog picker set (or stays null).
      let service_id      = form.service_id || null;
      let service_name_ar = form.service_name_ar.trim();
      let service_name_en = (form.service_name_en || form.service_name_ar).trim();
      let sub_service_id      = form.sub_service_id || null;
      let sub_service_name_ar = form.sub_service_name_ar || null;
      let sub_service_name_en = form.sub_service_name_en || null;
      if (form.sub_service_id === OTHER_ID) {
        sub_service_id      = null;
        sub_service_name_ar = form.custom_service_name.trim();
        sub_service_name_en = form.custom_service_name.trim();
      }
      if (form.service_id === OTHER_ID) {
        service_id      = null;
        service_name_ar = form.custom_service_name.trim();
        service_name_en = form.custom_service_name.trim();
        // If Main is Other, sub is meaningless.
        sub_service_id = sub_service_name_ar = sub_service_name_en = null;
      }
      const body = {
        target_type: targetType,
        target_id: form.target_id || null,
        target_name: form.target_name.trim(),
        target_phone: form.target_phone.trim() || null,
        target_email: form.target_email.trim() || null,
        service_id,
        service_name_ar,
        service_name_en,
        sub_service_id,
        sub_service_name_ar,
        sub_service_name_en,
        description_ar: form.description_ar.trim(),
        description_en: form.description_en.trim(),
        priority: form.priority,
        request_date: form.request_date || null,
        request_source: form.request_source,
        request_note: form.request_note.trim(),
        assigned_to_id: form.assigned_to_id || null,
        assigned_to_name: form.assigned_to_name || null,
        assigned_to_email: form.assigned_to_email || null,
        attachments: form.attachments,
        status: 'pending',
        // Feb 2026 (iter-183) — Fees moved to the detail page. Send empty
        // defaults so the backend schema still accepts the payload; the admin
        // configures service_fee, fee_model, and procedures/actions after
        // opening the created task.
        gov_procedures: [],
        service_fee: null,
        fee_model: 'service_only',
        total_price: null,
        // iter-188 — Requestor identity (AGENT tasks only).
        requestor_name:  targetType === 'agent' ? (form.requestor_name.trim() || null) : null,
        requestor_email: targetType === 'agent' ? (form.requestor_email.trim() || null) : null,
        requestor_phone: targetType === 'agent' ? (form.requestor_phone.trim() || null) : null,
      };
      const res = await axios.post(`${API_URL}/api/admin/assigned-tasks`, body, { headers: getAuthHeaders() });
      const created = res.data;
      setPreview({
        request_no_display: created.request_no_display,
        service_request_id: created.service_request_id,
        target_type: targetType,
      });
      toast.success(t(`✓ تم إنشاء الطلب رقم ${created.request_no_display}`, `✓ Request ${created.request_no_display} created`));
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل الإنشاء', 'Create failed'));
    } finally {
      setSubmitting(false);
    }
  };

  // ── Success view ────────────────────────────────────────
  if (preview) {
    // Feb 2026 — same back-target as the form's own back button.
    const backPath = isCustomer
      ? '/manage/cloudgm?tab=services-categories'
      : '/manage/cloudgm-business?tab=follow-agent-tasks';
    return (
      <div className="min-h-screen bg-[#030303]" dir={isAr ? 'rtl' : 'ltr'}>
        <DashboardHeader showBackButton backPath={backPath} />
        <main className="max-w-2xl mx-auto px-4 py-16">
          <div className="bg-white/[0.03] border border-emerald-500/30 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 border border-emerald-400/40 mx-auto mb-4 flex items-center justify-center">
              <UserCheck className="w-8 h-8 text-emerald-300" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              {t('✓ تم إدراج المهمة بنجاح', '✓ Task inserted successfully')}
            </h1>
            <div className="text-white/60 text-sm mb-1">
              {t('رقم الطلب المُنشأ في تتبع الطلبات', 'Request number in service tracker')}
            </div>
            <div className="text-3xl font-mono font-bold my-3" style={{ color: accentColor }}>
              {preview.request_no_display}
            </div>
            <div className="text-white/50 text-xs mb-6">
              {isCustomer
                ? t('الطلب تمت الموافقة عليه تلقائيًا وسيظهر في منصة العملاء ▸ طلبات الخدمة ضمن الفئة المختارة، وكذلك في حساب العميل بمجرد تسجيله بنفس البريد الإلكتروني.',
                    'The request was auto-approved and will appear under Customers Platform ▸ Service Requests within the picked category, and in the customer\'s account once they log in with the same email.')
                : t('الطلب تمت الموافقة عليه تلقائيًا وسيظهر في منصة التجار ▸ متابعة مهام الوكلاء، وكذلك في حساب الوكيل بمجرد تسجيله بنفس البريد الإلكتروني.',
                    'The task was auto-approved and will appear under Merchants Platform ▸ Track Agent Tasks, and in the agent\'s account once they log in with the same email.')}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Button onClick={() => navigate(backPath)} variant="outline"
                      className="border-white/10 bg-transparent text-white h-11">
                {isCustomer
                  ? t('عودة إلى طلبات الخدمة', 'Back to service requests')
                  : t('عودة إلى متابعة المهام', 'Back to task tracking')}
              </Button>
              <Button onClick={() => {
                        setPreview(null);
                        setForm({
                          target_id: null, target_name: '', target_phone: '', target_email: '',
                          service_id: '', service_name_ar: '', service_name_en: '',
                          sub_service_id: '', sub_service_name_ar: '', sub_service_name_en: '',
                          custom_service_name: '',
                          description_ar: '', description_en: '',
                          priority: 'medium', request_date: new Date().toISOString().slice(0, 10),
                          request_source: 'whatsapp',
                          request_note: '', assigned_to_id: '', assigned_to_name: '', assigned_to_email: '',
                          attachments: [],
                        });
                        setSearchQ('');
                      }}
                      className="text-black font-bold h-11"
                      style={{ backgroundColor: accentColor }}>
                {isCustomer
                  ? t('إضافة طلب آخر', 'Add another request')
                  : t('إدراج مهمة أخرى', 'Insert another task')}
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030303]" dir={isAr ? 'rtl' : 'ltr'}>
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] rounded-full blur-[150px]"
             style={{ backgroundColor: `${accentColor}10` }} />
      </div>

      {/* Feb 2026: no more separate hub — go back to the correct sub-platform:
           customer → منصة العملاء ▸ طلبات الخدمة
           agent    → منصة التجار ▸ الوكلاء ▸ مهام الوكلاء (the 3-card hub) */}
      <DashboardHeader showBackButton backPath={
        isCustomer
          ? '/manage/cloudgm?tab=services-categories'
          : '/manage/cloudgm-business?tab=agent-tasks'
      } />

      <div className="border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={
            isCustomer
              ? [
                  { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
                  { label_ar: 'CLOUDGM FZ LLC', label_en: 'CLOUDGM FZ LLC', path: '/dashboard?view=platform-control&sub=fzlc' },
                  { label_ar: 'منصة العملاء', label_en: 'Customers Platform', path: '/manage/cloudgm' },
                  { label_ar: 'طلبات الخدمة', label_en: 'Service Requests', path: '/manage/cloudgm?tab=services-categories' },
                  { label_ar: 'إضافة طلب لعميل', label_en: 'Add customer request', path: null },
                ]
              : [
                  { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
                  { label_ar: 'CLOUDGM FZ LLC', label_en: 'CLOUDGM FZ LLC', path: '/dashboard?view=platform-control&sub=fzlc' },
                  { label_ar: 'منصة التجار', label_en: 'Merchants Platform', path: '/manage/cloudgm-business' },
                  { label_ar: 'مهام الوكلاء', label_en: 'Agent Tasks', path: '/manage/cloudgm-business?tab=agent-tasks' },
                  { label_ar: 'إدراج مهمة لوكيل', label_en: 'Add agent task', path: null },
                ]
          } />
        </div>
      </div>

      <main className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex items-start gap-4">
          <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
               style={{ backgroundColor: `${accentColor}15`, border: `1px solid ${accentColor}40` }}>
            <TargetIcon className="w-7 h-7" style={{ color: accentColor }} />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-1">
              {isCustomer ? t('إضافة طلب لعميل', 'Add a customer request')
                          : t('إدراج مهمة لوكيل', 'Add an agent task')}
            </h1>
            <p className="text-white/50 text-sm">
              {isCustomer
                ? t('استخدم هذه الصفحة لتسجيل طلب عميل جاء يدوياً (واتساب / اتصال / حضور شخصي).',
                    'Use this page to log a customer request that came in manually (WhatsApp / phone / walk-in).')
                : t('استخدم هذه الصفحة لتسجيل مهمة وكيل جاءت يدوياً — دون أن يدخل الموقع بنفسه.',
                    'Use this page to log an agent task received manually — without them logging in themselves.')}
            </p>
          </div>
        </div>

        <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 space-y-6">
          {/* Target ─────────────────────────────────── */}
          <section>
            <h3 className="text-white font-bold text-sm mb-3">
              {isCustomer ? t('١) العميل المستهدف *', '1) Target customer *')
                          : t('١) الوكيل المستهدف *', '1) Target agent *')}
            </h3>

            {/* Agent mode toggle */}
            {!isCustomer && (
              <div className="flex items-center gap-2 mb-3 bg-[#050505] border border-white/10 rounded-lg p-1 max-w-md">
                <button
                  onClick={() => setAgentMode('registered')}
                  data-testid="agent-mode-registered"
                  className={`flex-1 h-9 rounded-md text-sm font-medium flex items-center justify-center gap-2 transition ${
                    agentMode === 'registered' ? 'bg-purple-500/25 text-white' : 'text-white/50 hover:text-white/80'
                  }`}>
                  <UserCheck className="w-4 h-4" /> {t('وكيل مسجل', 'Registered agent')}
                </button>
                <button
                  onClick={() => setAgentMode('new')}
                  data-testid="agent-mode-new"
                  className={`flex-1 h-9 rounded-md text-sm font-medium flex items-center justify-center gap-2 transition ${
                    agentMode === 'new' ? 'bg-purple-500/25 text-white' : 'text-white/50 hover:text-white/80'
                  }`}>
                  <UserPlus className="w-4 h-4" /> {t('وكيل جديد (غير مسجل)', 'New (unregistered)')}
                </button>
              </div>
            )}

            {/* Search (registered scope) */}
            {(isCustomer || agentMode === 'registered') && (
              <div className="mb-3 relative">
                <label className="block text-white/50 text-xs mb-1.5">
                  {isCustomer ? t('بحث في قاعدة العملاء', 'Search customer database')
                              : t('بحث في قاعدة الوكلاء', 'Search agent database')}
                </label>
                <div className="relative">
                  <Search className="absolute inset-y-0 start-3 my-auto w-4 h-4 text-white/40" />
                  <Input value={searchQ}
                         onChange={(e) => setSearchQ(e.target.value)}
                         placeholder={t('اسم / بريد / هاتف', 'Name / email / phone')}
                         className="bg-[#050505] border-white/10 text-white ps-9"
                         data-testid="task-target-search" />
                </div>
                {searching && <div className="text-white/40 text-xs mt-1">{t('جارٍ البحث…', 'Searching…')}</div>}
                {searchResults.length > 0 && (
                  <div className="absolute z-20 top-full inset-x-0 mt-1 rounded-lg bg-[#0f0f0f] border border-white/10 max-h-56 overflow-y-auto shadow-2xl">
                    {searchResults.map((u) => (
                      <button key={u.id} onClick={() => pickTarget(u)}
                              className="w-full text-start px-3 py-2 hover:bg-white/5 text-sm border-b border-white/5 last:border-0"
                              data-testid={`task-target-result-${u.id}`}>
                        <div className="text-white">{u.name || u.email}</div>
                        {u.email && <div className="text-white/40 text-xs">{u.email}</div>}
                        {u.phone && <div className="text-white/40 text-xs" dir="ltr">{u.phone}</div>}
                      </button>
                    ))}
                  </div>
                )}
                {form.target_id && (
                  <div className="mt-2 text-xs text-emerald-400 flex items-center gap-1">
                    <UserCheck className="w-3 h-3" />
                    {t('تم اختيار مستخدم مسجل', 'Registered user selected')} — ID: <span className="font-mono">{form.target_id.slice(0, 8)}…</span>
                  </div>
                )}
              </div>
            )}

            {/* Show input fields */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label={t('الاسم', 'Name') + ' *'}>
                <Input value={form.target_name}
                       readOnly={isCustomer || agentMode === 'registered' ? !!form.target_id : false}
                       onChange={(e) => setForm(f => ({ ...f, target_name: e.target.value }))}
                       className="bg-[#050505] border-white/10 text-white"
                       data-testid="task-target-name" />
              </Field>
              <Field label={t('الهاتف', 'Phone') + (!isCustomer && agentMode === 'new' ? ' *' : '')}>
                <Input value={form.target_phone}
                       readOnly={isCustomer || agentMode === 'registered' ? !!form.target_id : false}
                       onChange={(e) => setForm(f => ({ ...f, target_phone: e.target.value }))}
                       className="bg-[#050505] border-white/10 text-white" dir="ltr"
                       placeholder="+971 50 123 4567"
                       data-testid="task-target-phone" />
              </Field>
              <Field label={t('البريد الإلكتروني', 'Email') + (!isCustomer && agentMode === 'new' ? ' *' : '')}>
                <Input value={form.target_email} type="email"
                       readOnly={isCustomer || agentMode === 'registered' ? !!form.target_id : false}
                       onChange={(e) => setForm(f => ({ ...f, target_email: e.target.value }))}
                       className="bg-[#050505] border-white/10 text-white" dir="ltr"
                       data-testid="task-target-email" />
              </Field>
            </div>

            {!isCustomer && agentMode === 'new' && (
              <div className="mt-2 text-[11px] text-white/50 flex items-center gap-1">
                <MessageCircle className="w-3 h-3" />
                {t('بيانات الوكيل الجديد ستُحفظ. عندما يسجّل لاحقًا بنفس البريد سيرى المهمة ورقمها ضمن سجل حسابه.',
                   'The new agent\'s details are saved. When they later register with the same email, they will see this task and its number under their account history.')}
              </div>
            )}
          </section>

          {/* Requestor section — AGENT tasks only.  iter-188 (Feb 2026) —
              Captures the person who is submitting the request on behalf
              of the target agent (an employee, a family member, etc). All
              fields are optional; email + phone are format-validated. */}
          {targetType === 'agent' && (
            <section data-testid="requestor-section">
              <h3 className="text-white font-bold text-sm mb-3">
                {t('١.٥) بيانات المقدم لأجل هذا الطلب', '1.5) Requestor details')}
                <span className="ms-2 text-white/40 text-[11px] font-normal">
                  {t('(اختياري)', '(optional)')}
                </span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label={t('الاسم الكامل', 'Full name')}>
                  <Input
                    value={form.requestor_name}
                    onChange={(e) => setForm(f => ({ ...f, requestor_name: e.target.value }))}
                    placeholder={t('مثلاً: أحمد محمد', 'e.g. Ahmed Mohammed')}
                    className="bg-[#050505] border-white/10 text-white"
                    data-testid="requestor-name"
                  />
                </Field>
                <Field label={t('البريد الإلكتروني', 'Email')}>
                  <Input
                    type="email"
                    value={form.requestor_email}
                    onChange={(e) => setForm(f => ({ ...f, requestor_email: e.target.value }))}
                    placeholder="name@example.com"
                    dir="ltr"
                    className={`bg-[#050505] text-white ${
                      form.requestor_email && !isValidEmail(form.requestor_email)
                        ? 'border-red-500/60 focus-visible:ring-red-500'
                        : 'border-white/10'
                    }`}
                    data-testid="requestor-email"
                  />
                  {form.requestor_email && !isValidEmail(form.requestor_email) && (
                    <div className="text-[11px] text-red-400 mt-1">
                      {t('البريد الإلكتروني غير صحيح', 'Invalid email address')}
                    </div>
                  )}
                </Field>
                <Field label={t('رقم الهاتف', 'Phone number')}>
                  <Input
                    value={form.requestor_phone}
                    onChange={(e) => setForm(f => ({ ...f, requestor_phone: e.target.value }))}
                    placeholder="+971 50 000 0000"
                    dir="ltr"
                    className={`bg-[#050505] text-white ${
                      form.requestor_phone && !isValidPhone(form.requestor_phone)
                        ? 'border-red-500/60 focus-visible:ring-red-500'
                        : 'border-white/10'
                    }`}
                    data-testid="requestor-phone"
                  />
                  {form.requestor_phone && !isValidPhone(form.requestor_phone) && (
                    <div className="text-[11px] text-red-400 mt-1">
                      {t('رقم الهاتف غير صحيح', 'Invalid phone number')}
                    </div>
                  )}
                </Field>
              </div>
            </section>
          )}

          {/* Service ─────────────────────────────── */}
          <section>
            <h3 className="text-white font-bold text-sm mb-3">{t('٢) تفاصيل الخدمة *', '2) Service details *')}</h3>
            <div className="space-y-3">
              {/* Feb 2026 — unified main/sub picker for BOTH scopes (customer +
                  agent). Lists come from /api/admin/service-catalog/{scope}
                  which is admin-editable. An "أخرى" pseudo-option is always
                  appended so a one-off service can be logged without polluting
                  the catalog. */}
              <Field label={t('الخدمة الرئيسية', 'Main service') + ' *'}>
                <select value={form.service_id || ''}
                        onChange={(e) => pickService(e.target.value)}
                        dir={isAr ? 'rtl' : 'ltr'}
                        className="w-full bg-[#050505] border border-white/10 text-white rounded-md h-10 px-2 text-sm [color-scheme:dark]"
                        data-testid="task-main-service">
                  <option value="" style={{ backgroundColor: '#050505', color: '#fff' }}>
                    {t('— اختر الخدمة الرئيسية —', '— Pick the main service —')}
                  </option>
                  {catalog.map(s => (
                    <option key={s.id} value={s.id} style={{ backgroundColor: '#050505', color: '#fff' }}>
                      {isAr ? s.name_ar : (s.name_en || s.name_ar)}
                    </option>
                  ))}
                  <option value={OTHER_ID} style={{ backgroundColor: '#050505', color: '#fff' }}>
                    {t('أخرى (إدخال يدوي)', 'Other (manual entry)')}
                  </option>
                </select>
                {catalog.length === 0 && (
                  <div className="text-[11px] text-amber-400 mt-1">
                    {t('القائمة فارغة — أضف الخدمات من "القوائم المنسدلة" في التحكم.',
                       'The list is empty — add services from "Dropdown lists" in the control section.')}
                  </div>
                )}
              </Field>

              <Field label={t('الخدمة الفرعية', 'Sub-service')}>
                {(() => {
                  const parent = catalog.find(s => s.id === form.service_id);
                  const subs   = parent?.sub_services || [];
                  const mainIsOther = form.service_id === OTHER_ID;
                  // Sub dropdown is only meaningful when a real main is picked
                  // (or when main = Other → straight to free-text).
                  const disabled = !form.service_id || (mainIsOther) || (!mainIsOther && subs.length === 0);
                  return (
                    <>
                      <select value={form.sub_service_id || ''}
                              onChange={(e) => pickSubService(e.target.value)}
                              dir={isAr ? 'rtl' : 'ltr'}
                              disabled={disabled}
                              className="w-full bg-[#050505] border border-white/10 text-white rounded-md h-10 px-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed [color-scheme:dark]"
                              data-testid="task-sub-service">
                        <option value="" style={{ backgroundColor: '#050505', color: '#fff' }}>
                          {!form.service_id
                            ? t('— اختر الخدمة الرئيسية أولاً —', '— Pick a main service first —')
                            : mainIsOther
                              ? t('— اكتب اسم الخدمة أدناه —', '— Enter the service name below —')
                              : subs.length === 0
                                ? t('— لا توجد خدمات فرعية —', '— No sub-services —')
                                : t('— اختر الخدمة الفرعية (اختياري) —', '— Pick a sub-service (optional) —')}
                        </option>
                        {!mainIsOther && subs.map(s => (
                          <option key={s.id} value={s.id} style={{ backgroundColor: '#050505', color: '#fff' }}>
                            {isAr ? s.name_ar : (s.name_en || s.name_ar)}
                          </option>
                        ))}
                        {!mainIsOther && form.service_id && (
                          <option value={OTHER_ID} style={{ backgroundColor: '#050505', color: '#fff' }}>
                            {t('أخرى (إدخال يدوي)', 'Other (manual entry)')}
                          </option>
                        )}
                      </select>
                      {form.service_id && !mainIsOther && subs.length === 0 && (
                        <div className="text-[11px] text-white/40 mt-1">
                          {t('لم تُضبط قائمة الخدمات الفرعية لهذه الفئة بعد — يمكن تعبئتها لاحقاً من صفحة القوائم المنسدلة.',
                             'The sub-service list for this category has not been configured yet — you can populate it later from the Dropdown Lists page.')}
                        </div>
                      )}
                    </>
                  );
                })()}
              </Field>

              {/* Free-text override when the admin picks "أخرى" */}
              {(form.service_id === OTHER_ID || form.sub_service_id === OTHER_ID) && (
                <Field label={t('اسم الخدمة (يدوي)', 'Service name (manual)') + ' *'}>
                  <Input value={form.custom_service_name}
                         onChange={(e) => setForm(f => ({ ...f, custom_service_name: e.target.value }))}
                         placeholder={t('اكتب اسم الخدمة كما جاء من العميل…',
                                         'Type the service name exactly as the customer described it…')}
                         dir={isAr ? 'rtl' : 'ltr'}
                         className="bg-[#050505] border-white/10 text-white h-10"
                         data-testid="task-custom-service-name" />
                  <div className="text-[11px] text-amber-400/80 mt-1">
                    {t('حقل إجباري — سيُستخدم كاسم الخدمة الظاهر في الطلب.',
                       'Required — this text will be used as the visible service name on the request.')}
                  </div>
                </Field>
              )}

              {/* Feb 2026 (iter-183) — Fees section removed from the creation
                   form per user spec. Admin picks Service + Sub-service +
                   Notes here; the "Service Fees" card + procedures / receipts
                   are configured from the task detail page after creation. */}

              <Field label={t('ملاحظات على الطلب (اختياري)', 'Extra description (optional)')}>
                <textarea value={form.description_ar}
                          onChange={(e) => setForm(f => ({ ...f, description_ar: e.target.value }))}
                          rows={3}
                          placeholder={t('تفاصيل إضافية عن الخدمة أو الطلب…',
                                          'Additional service or request details…')}
                          className="w-full bg-[#050505] border border-white/10 text-white rounded-md p-2.5 text-sm resize-y focus:outline-none focus:border-[#d4af37]/60"
                          dir={isAr ? 'rtl' : 'ltr'}
                          data-testid="task-desc-ar" />
              </Field>
            </div>
          </section>

          {/* Request properties ────────────────── */}
          <section>
            <h3 className="text-white font-bold text-sm mb-3">{t('٣) خصائص الطلب', '3) Request properties')}</h3>

            {/* Priority chips (4 levels) */}
            <div className="mb-4">
              <label className="block text-white/50 text-xs mb-2">{t('الأولوية', 'Priority')}</label>
              <div className="flex flex-wrap gap-2">
                {PRIORITY_LEVELS.map(p => {
                  const active = form.priority === p.key;
                  return (
                    <button key={p.key}
                            type="button"
                            onClick={() => setForm(f => ({ ...f, priority: p.key }))}
                            data-testid={`task-priority-${p.key}`}
                            className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition ${
                              active ? 'text-black' : 'text-white/70 bg-[#0a0a0a] border-white/10 hover:border-white/25'
                            }`}
                            style={active ? { backgroundColor: p.color, borderColor: p.color } : {}}>
                      {isAr ? p.ar : p.en}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label={t('مصدر الطلب', 'Request source')}>
                <select value={form.request_source}
                        onChange={(e) => setForm(f => ({ ...f, request_source: e.target.value }))}
                        dir={isAr ? 'rtl' : 'ltr'}
                        className="w-full bg-[#050505] border border-white/10 text-white rounded-md h-10 px-2 text-sm"
                        data-testid="task-source">
                  {REQUEST_SOURCES.map(s => (
                    <option key={s.key} value={s.key}>{isAr ? s.ar : s.en}</option>
                  ))}
                </select>
              </Field>
              <Field label={t('تاريخ تقديم الطلب', 'Request submission date')}>
                <Input type="date"
                       value={form.request_date}
                       onChange={(e) => setForm(f => ({ ...f, request_date: e.target.value }))}
                       className="bg-[#050505] border-white/10 text-white" dir="ltr"
                       data-testid="task-request-date" />
                <div className="text-[11px] text-white/40 mt-1">
                  {t('التاريخ اللي وصلك فيه الطلب من العميل (قد يكون قبل اليوم)',
                     'The date the customer originally submitted the request (may be in the past)')}
                </div>
              </Field>
            </div>

            <div className="mt-3">
              <Field label={t('ملاحظات داخلية (اختياري — للفريق فقط)', 'Internal notes (optional — team only)')}>
                <textarea value={form.request_note}
                          onChange={(e) => setForm(f => ({ ...f, request_note: e.target.value }))}
                          rows={3}
                          placeholder={t('نسخ من محادثة الواتساب أو أي تفاصيل داخلية…',
                                         'Paste the WhatsApp conversation or any internal context…')}
                          className="w-full bg-[#050505] border border-white/10 text-white rounded-md p-2.5 text-sm resize-y focus:outline-none focus:border-[#d4af37]/60"
                          dir={isAr ? 'rtl' : 'ltr'}
                          data-testid="task-request-note" />
                <div className="text-[11px] text-white/40 mt-1 flex items-center gap-1">
                  <MessageCircle className="w-3 h-3" />
                  {t('لا يظهر للمستخدم — يبقى داخلياً في إدارة المهام', 'Hidden from the user — kept internal')}
                </div>
              </Field>
            </div>
          </section>

          {/* Executor ─────────────────────────── */}
          <section>
            <h3 className="text-white font-bold text-sm mb-3">{t('٤) منفذ المهمة *', '4) Task executor *')}</h3>
            <Field label={t('تحويل المهمة إلى', 'Assign task to') + ' *'}>
              <select value={form.assigned_to_id}
                      onChange={(e) => pickExecutor(e.target.value)}
                      dir={isAr ? 'rtl' : 'ltr'}
                      disabled={staffLoading}
                      className="w-full bg-[#050505] border border-white/10 text-white rounded-md h-10 px-2 text-sm disabled:opacity-60"
                      data-testid="task-executor">
                <option value="">
                  {staffLoading
                    ? t('… جاري تحميل الموظفين', '… Loading staff members')
                    : t('— اختر مسؤولاً / موظفاً —', '— Pick a staff member —')}
                </option>
                {staff.map(p => (
                  <option key={p.id} value={p.id}>
                    {(p.name || p.email)} {p.role ? `— ${p.role}` : ''}
                  </option>
                ))}
              </select>
              {/* iter-193 — surface any load error explicitly + one-click retry */}
              {!staffLoading && (staffLoadError || staff.length === 0) && (
                <div className="mt-2 text-[11px] px-3 py-2 rounded-md bg-red-500/10 border border-red-500/25 text-red-300 flex items-center justify-between gap-2"
                     data-testid="staff-load-error">
                  <span>
                    {t('تعذّر تحميل قائمة الموظفين. حاول مرة أخرى.',
                       'Could not load the staff list. Please retry.')}
                  </span>
                  <button type="button"
                          onClick={loadStaff}
                          className="px-2 py-1 rounded-md bg-red-500/20 hover:bg-red-500/30 text-red-100 text-[11px] font-bold"
                          data-testid="staff-load-retry">
                    {t('إعادة المحاولة', 'Retry')}
                  </button>
                </div>
              )}
              <div className="text-[11px] text-white/40 mt-1">
                {t('المهمة ستظهر في "قائمة مهامي" عند الشخص المُختار.',
                   'The task will appear in the selected person\'s "My tasks" list.')}
              </div>
            </Field>
          </section>

          {/* Attachments ─────────────────────── */}
          <section>
            <h3 className="text-white font-bold text-sm mb-3 flex items-center gap-2">
              <Paperclip className="w-4 h-4" />
              {t('٥) المرفقات', '5) Attachments')}
              <span className="text-white/40 text-xs font-normal">
                {t(`(اختياري — حتى ${MAX_ATTACHMENTS} ملف، PDF أو صور)`,
                   `(optional — up to ${MAX_ATTACHMENTS} files, PDF or images)`)}
              </span>
            </h3>

            {/* Attachment list */}
            {form.attachments.length > 0 && (
              <div className="space-y-2 mb-3">
                {form.attachments.map((a, idx) => (
                  <div key={idx}
                       className="flex items-center gap-2 bg-[#050505] border border-white/10 rounded-lg p-2"
                       data-testid={`task-attachment-${idx}`}>
                    <div className="flex-shrink-0 w-9 h-9 rounded bg-white/5 flex items-center justify-center">
                      {a.mime_type?.startsWith('image/')
                        ? <ImageIcon className="w-4 h-4 text-blue-300" />
                        : <FileText className="w-4 h-4 text-red-300" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <Input value={a.label}
                             onChange={(e) => renameAttachment(idx, e.target.value)}
                             placeholder={t('اسم المرفق (مطلوب) — مثال: صورة جواز', 'Attachment name (required) — e.g. passport photo')}
                             className={`bg-transparent text-white h-8 text-sm ${
                               (a.label || '').trim() === '' ? 'border-amber-500/60 focus:border-amber-400' : 'border-white/10'
                             }`}
                             data-testid={`task-attachment-name-${idx}`} />
                      <div className="text-[10px] text-white/40 mt-0.5 truncate">
                        {a.name} · {(a.size / 1024).toFixed(0)} KB
                      </div>
                    </div>
                    <button
                      onClick={() => removeAttachment(idx)}
                      className="p-1.5 rounded hover:bg-red-500/10 text-red-400/70 hover:text-red-400"
                      data-testid={`task-attachment-remove-${idx}`}>
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add button */}
            {form.attachments.length < MAX_ATTACHMENTS && (
              <label
                htmlFor="task-attachment-input"
                className="inline-flex items-center gap-2 cursor-pointer px-3 py-2 rounded-lg border border-dashed border-white/20 hover:border-white/40 hover:bg-white/5 text-sm text-white/70"
                data-testid="task-attachment-add">
                <Plus className="w-4 h-4" />
                {t('إضافة مرفق', 'Add attachment')}
                <span className="text-white/40 text-xs">
                  ({form.attachments.length}/{MAX_ATTACHMENTS})
                </span>
              </label>
            )}
            <input id="task-attachment-input" type="file" multiple hidden
                   accept="application/pdf,image/png,image/jpeg,image/webp,image/heic,image/heif"
                   onChange={(e) => { onAttachmentPicked(e.target.files); e.target.value = ''; }} />
          </section>

          {/* Submit ─────────────────────────────────── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
            <Button variant="outline"
                    onClick={() => navigate('/dashboard/cloudgm/assigned-tasks')}
                    className="border-white/10 bg-transparent text-white/80 hover:bg-white/5 h-12"
                    data-testid="task-cancel">
              <ArrowLeft className="w-4 h-4 me-2" />
              {t('إلغاء والعودة', 'Cancel & back')}
            </Button>
            <Button onClick={submit} disabled={!canSubmit}
                    className="text-black font-bold h-12 disabled:opacity-50"
                    style={{ backgroundColor: accentColor }}
                    data-testid="task-submit">
              {submitting
                ? <><Loader2 className="w-4 h-4 animate-spin me-2" />{t('جارٍ الحفظ...', 'Saving...')}</>
                : <><Plus className="w-4 h-4 me-2" />{t('إدراج المهمة والموافقة تلقائياً', 'Insert & auto-approve')}</>}
            </Button>
          </div>
          {/* Feb 2026 — iter-179: still show the checklist for at-a-glance guidance,
               but the button is now always clickable so the user gets an explicit
               toast per missing field instead of a silent no-op. */}
          {!submitting && (!targetOk || !serviceOk || !executorOk || !attachmentsOk) && (
            <div className="text-xs text-amber-400/80 text-center bg-amber-500/5 border border-amber-500/20 rounded-lg py-2 px-3">
              <div className="font-semibold mb-0.5">{t('قبل الحفظ يجب:', 'Before saving:')}</div>
              {!targetOk       && <div>{t('• حدد المستهدف المطلوب (اختره من قائمة البحث للعميل / الوكيل المسجّل)', '• Pick a valid target (from search list for a registered customer/agent)')}</div>}
              {targetOk && !serviceOk && <div>{t('• اختر الخدمة', '• Pick the service')}</div>}
              {targetOk && serviceOk && !executorOk && <div>{t('• اختر منفذ المهمة — الطلب لن يصل لأي أدمن بدونه', '• Pick a task executor — task will not reach any admin without one')}</div>}
              {targetOk && serviceOk && executorOk && !attachmentsOk && <div>{t('• اكتب اسم لكل مرفق', '• Enter a name for every attachment')}</div>}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}


function Field({ label, children }) {
  return (
    <div>
      <label className="block text-white/70 text-xs mb-1.5">{label}</label>
      {children}
    </div>
  );
}
